import { useEffect, useRef, useState } from "react";
import { Eye, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, EmptyState } from "@/components/Logo";
import Dropzone from "@/components/invoices/Dropzone";
import InvoiceDetailDialog from "@/components/invoices/InvoiceDetailDialog";
import InvoiceReviewDialog from "@/components/invoices/InvoiceReviewDialog";
import api, { errMsg } from "@/lib/api";
import { eur, dateFr } from "@/lib/format";
import { t } from "@/i18n";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  const load = () => api.get("/invoices").then(({ data }) => setInvoices(data));
  useEffect(() => { load(); return () => clearInterval(timer.current); }, []);

  const upload = async (file) => {
    setBusy(true);
    setStep(0);
    timer.current = setInterval(() => setStep((s) => Math.min(s + 1, 3)), 6000);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/extract", fd, { timeout: 180000 });
      setDraft(data);
    } catch (e) {
      toast.error(errMsg(e, t("invoices.l_extraction_a_echoue")));
    } finally {
      clearInterval(timer.current);
      setBusy(false);
    }
  };

  const confirm = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.post("/invoices/confirm", body);
      const s = data.summary;
      toast.success(t("invoices.facture_x_importee_x_produit_s", { v0: data.supplier_name, v1: data.lines.length }), {
        description: [s.increases && t("invoices.x_hausse_s_de_prix_detectee_s", { v0: s.increases }), s.recipes_recalculated && t("invoices.x_recette_s_recalculee_s", { v0: s.recipes_recalculated })].filter(Boolean).join(" · ") || undefined,
      });
      setDraft(null);
      setDetail(data);
      load();
      window.dispatchEvent(new Event("alerts:changed"));
    } catch (e) {
      toast.error(errMsg(e, t("invoices.l_enregistrement_a_echoue")));
    } finally {
      setSaving(false);
    }
  };

  const open = async (id) => {
    const { data } = await api.get(`/invoices/${id}`);
    setDetail(data);
  };

  const remove = async (id) => {
    if (!window.confirm(t("invoices.supprimer_cette_facture_les_prix_associes"))) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("invoices.facture_supprimee"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <>
      <PageHeader eyebrow={t("invoices.import_ia")} title={t("invoices.factures_fournisseurs")} description={t("invoices.importez_vos_factures_l_ia_extrait_les_pro")} />
      <Dropzone onFile={upload} busy={busy} step={step} />
      <div className="mt-8">
        <h2 className="mb-3 text-lg font-semibold">{t("invoices.historique_des_factures")}</h2>
        {invoices.length === 0 ? (
          <EmptyState icon={FileText} title={t("invoices.aucune_facture_importee")} description={t("invoices.votre_premiere_facture_alimentera_automati")} testId="invoices-empty" />
        ) : (
          <>
          <div className="space-y-2 md:hidden" data-testid="invoice-mobile-list">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-2 rounded-xl border bg-card p-3.5" data-testid={`invoice-card-${inv.id}`}>
                <button type="button" onClick={() => open(inv.id)} className="min-w-0 flex-1 text-left" data-testid={`invoice-card-open-${inv.id}`}>
                  <p className="truncate font-medium">{inv.supplier_name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{dateFr(inv.invoice_date)} · {t("common.n_lines_paren", { v0: inv.line_count })}</p>
                </button>
                <p className="num shrink-0 text-sm font-semibold">{eur(inv.total_amount)}</p>
                <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => remove(inv.id)} data-testid={`invoice-card-delete-${inv.id}`} aria-label={t("common.supprimer")}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <div className="hidden overflow-hidden rounded-xl border bg-card md:block">
            <Table data-testid="invoice-list-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.fournisseur")}</TableHead>
                  <TableHead>{t("invoices.date")}</TableHead>
                  <TableHead className="text-right">{t("invoices.montant_ht")}</TableHead>
                  <TableHead className="hidden text-right sm:table-cell">{t("invoices.lignes")}</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} className="cursor-pointer" onClick={() => open(inv.id)} data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="font-medium">{inv.supplier_name}</TableCell>
                    <TableCell className="text-muted-foreground">{dateFr(inv.invoice_date)}</TableCell>
                    <TableCell className="num text-right">{eur(inv.total_amount)}</TableCell>
                    <TableCell className="num hidden text-right sm:table-cell">{inv.line_count}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => open(inv.id)} data-testid={`invoice-view-btn-${inv.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(inv.id)} data-testid={`invoice-delete-btn-${inv.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>
      <InvoiceDetailDialog invoice={detail} onClose={() => setDetail(null)} />
      <InvoiceReviewDialog draft={draft} saving={saving} onCancel={() => setDraft(null)} onConfirm={confirm} />
    </>
  );
}
